import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Roseville", cities: "Blue Oaks, Diamond Oaks, Westpark, Fiddyment Farm" },
    { zone: "Rocklin", cities: "Whitney Ranch, Stanford Ranch, Sunset Whitney" },
    { zone: "Granite Bay", cities: "Granite Bay, Loomis, Auburn" },
    { zone: "Citrus Heights", cities: "Citrus Heights, Fair Oaks, Orangevale" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos Cerca de Roseville</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo el corredor Placer County.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Roseville</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const RosevilleSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "roseville", name: "Roseville", type: "city", regionSlug: "sacramento-area" }}>
      <SEO
        title="Limpieza del Hogar en Roseville, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Roseville, CA. Precios planos y transparentes, reserva en 60 segundos, garantía de satisfacción del 200%. Asegurados y bonificados."
        keywords="limpieza del hogar roseville, servicio de limpieza roseville ca, limpieza profunda roseville, empleada doméstica roseville, limpieza de casas roseville california, maid service roseville español"
        url="https://uplevelcleaning.com/es/roseville"
        locationName="Roseville"
        regionSlug="sacramento-area"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Roseville", url: "/es/roseville" }
        ]}
        reviews={[
          { author: "Carmen R.", rating: 5, reviewBody: "¡Increíble servicio! Reservé en línea en minutos y llegaron puntuales. Mi casa quedó reluciente. ¡Los recomiendo a todos!", datePublished: "2026-03-18" },
          { author: "Hector M.", rating: 5, reviewBody: "El equipo fue muy profesional y detallista. Limpiaron hasta los rincones más difíciles. Precio justo y transparente.", datePublished: "2026-02-22" },
          { author: "Patricia V.", rating: 5, reviewBody: "Con tres hijos, siempre necesito ayuda con la limpieza. Uplevel Cleaning es confiable, puntual y dejan todo perfecto.", datePublished: "2026-01-30" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Roseville" regionSlug="sacramento-area" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {["Servicio Local en Roseville", "Limpiadores Verificados", "Garantía del 200%", "Precios Transparentes", "Reserva en 60 Segundos"].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Roseville" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="roseville" />
          <Guarantee />
          <SpanishFAQ cityName="Roseville" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Roseville" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default RosevilleSpanishPage;
